import { GeneralInfoEditCameraTab } from "../GeneralInfoEditCameraTab";
import { ConnectionsTab } from "../ConnectionsTab";
import { NotificationsTab } from "../NotificationsTab";
import { ProductsTab } from "../ProductsTab";
import { AxiosError, AxiosResponse } from "axios";
import { createApi } from "../../../utils/createApi";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { handleEditCameraCancelButtonClick } from "./handleEditCameraCancelButtonClick";
import { IgnoreZonesBreadCrumbs } from "../../EditIgnoreZones/IgnoreZonesBreadCrumbs";
import { createIgnoreZonesLinksArray } from "../../EditIgnoreZones/EditIgnoreZonesUtils/createIgnoreZonesLinksArray";
import { matchAllStreams } from "./numericallySortAlerts";
import { generateActiveProducts } from "./generateActiveProducts";
import { Dispatcher } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { CameraInfoRow } from "../../Sites/SiteAboutPage/CameraInfoRow";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import {
  isResourceNotFoundError,
  navigateToResourceNotFound,
} from "../../../utils/resourceNotFound";
import { MobotixTab } from "../MobotixTab";
import ErrorBoundary from "../../../../ErrorBoundary";
import { AxisTab } from "../AxisTab";
import { SentinelAlertsTab } from "../SentinelAlertsTab";
import { BoldAlertsTab } from "../BoldAlertsTab";
import { PatriotAlertsTab } from "../PatriotAlertsTab";
import { USMonitoringAlertsTab } from "../USMonitoringAlertsTab";
import { getNumbersAfterEditCamera, getNumbersAfterSites } from "../EditCamera";
import { SoftguardAlertsTab } from "../SoftguardAlertsTab";
import { StreamParametersTab } from "../StreamParametersTab";
import { AlertSettingsTab } from "../AlertSettingsTab";
import { useEditCameraStore } from "../editCameraStore/editCameraStore";
import { logger } from "../../../../utils/logger";
import { editCameraIdFromUrl } from "./urlUtils";
import { addIgnoreLabel } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/addIgnoreLabel";
import { useSiteAboutStore } from "../../Sites/SiteAboutPage/siteAboutStore/siteAboutStore";
import { SlicingTab } from "../SlicingTab";
import { VlmTab } from "../VlmTab";
import { getDefaultAlarmName } from "../../../utils/getDefaultAlarmName";
import { buildConnectionsTitlesArray } from "./connectionColumns";
import {
  clearEditCameraSaveProgress,
  EditCameraSaveProgressHandlers,
  reportEditCameraSaveProgress,
  reportEditCameraSaveRefreshing,
  reportEditCameraSaveStart,
  reportProductMetricsSaveProgress,
} from "./editCameraSaveProgress";
import {
  resolveMetricOptionIdForSubmit,
  shouldApplyAutopatrolHighSensitivityDefault,
} from "./autopatrolDefaultHighSensitivity";
import {
  isValidLivestreamUrl,
  LIVESTREAM_URL_ERROR_MESSAGE,
} from "./livestreamUrlUtils";
import { hasDoubleTakeChanged } from "./doubleTake";

/**
 * When patriot alerts were patched via /patriot/:id/, the general_info PATCH
 * must not include streams so it does not overwrite that alert data.
 *
 * `allowed_media_types` is never sent: the Camera audio card
 * (CameraAudioSetting, ENG-2204) owns it and saves it on its own, and the
 * patch object here is a page-load snapshot of general_info — echoing it back
 * would silently revert a value the card just saved.
 * Exported for unit tests.
 */
export function buildGeneralInfoPayload(
  updatedPatchObject: Record<string, unknown>,
  patriotAlertsWerePatched: boolean,
  chmValueChanged: boolean
): Record<string, unknown> {
  const { allowed_media_types: _allowedMediaTypes, ...payload } =
    updatedPatchObject;
  if (patriotAlertsWerePatched && !chmValueChanged) {
    const { streams: _streams, ...rest } = payload;
    return rest;
  }
  return payload;
}

type ProductMetricChange = {
  metricKey: string;
  productName: string;
  metricLabelName: string;
  currentMetric: Record<string, any>;
  previousMetric: Record<string, any> | null;
};

const createMetricKey = (metric: Record<string, any>) => {
  if (metric?.id) {
    return `id:${metric.id}`;
  }
  return `label:${metric?.label_name}`;
};

const hasMetricChanged = (
  currentMetric: Record<string, any>,
  previousMetric: Record<string, any> | undefined
) => {
  if (!previousMetric) {
    return true;
  }

  if (currentMetric.active !== previousMetric.active) {
    return true;
  }

  // DoubleTake is an explicit per-product switch, so a toggle counts even on
  // an inactive product (the guard below only skips stale tuning values).
  if (hasDoubleTakeChanged(currentMetric, previousMetric)) {
    return true;
  }

  // When a metric remains inactive, stale UI/default values for tuning fields
  // should not generate API updates.
  if (!currentMetric.active && !previousMetric.active) {
    return false;
  }

  const currentOptionId =
    currentMetric.option_id === undefined
      ? currentMetric.active
        ? null
        : previousMetric.option_id
      : currentMetric.option_id;
  const currentUserInputValue =
    currentMetric.user_input_value === undefined
      ? previousMetric.user_input_value
      : currentMetric.user_input_value;
  const currentDistance =
    currentMetric.distance === undefined
      ? previousMetric.distance
      : currentMetric.distance;
  const currentDistanceOptions =
    currentMetric.distance_options === undefined
      ? previousMetric.distance_options
      : currentMetric.distance_options;

  return (
    currentOptionId !== previousMetric.option_id ||
    currentUserInputValue !== previousMetric.user_input_value ||
    currentDistance !== previousMetric.distance ||
    JSON.stringify(currentDistanceOptions) !==
      JSON.stringify(previousMetric.distance_options)
  );
};

export const getChangedProductMetrics = (
  productsData: any[],
  backupProductsData: any[]
): ProductMetricChange[] => {
  const backupMetricMap = new Map<string, Record<string, any>>();

  backupProductsData.forEach((product: any) => {
    Object.values(product.metrics || {}).forEach((metric: any) => {
      const metricAsRecord = metric as Record<string, any>;
      const metricKey = createMetricKey(metricAsRecord);
      backupMetricMap.set(metricKey, metricAsRecord);
    });
  });

  const metricChanges: ProductMetricChange[] = [];

  productsData.forEach((product: any) => {
    Object.values(product.metrics || {}).forEach((metric: any) => {
      const metricAsRecord = metric as Record<string, any>;
      const metricKey = createMetricKey(metricAsRecord);
      const previousMetric = backupMetricMap.get(metricKey);

      if (hasMetricChanged(metricAsRecord, previousMetric)) {
        metricChanges.push({
          metricKey,
          productName: product.name,
          metricLabelName: metricAsRecord.label_name,
          currentMetric: metricAsRecord,
          previousMetric: previousMetric || null,
        });
      }
    });
  });

  return metricChanges;
};

export const buildMetricUpdatePayload = (
  currentMetric: Record<string, any>,
  previousMetric: Record<string, any> | null,
  isPostRequest: boolean,
  applyAutopatrolHighSensitivityDefault = false
) => {
  const getPostValue = (
    currentValue: any,
    previousValue: any,
    fallback: any = null
  ) => currentValue ?? previousValue ?? fallback;
  const payload: Record<string, any> = {
    label_name: currentMetric.label_name,
    active: currentMetric.active,
  };

  if (isPostRequest || currentMetric.option_id !== previousMetric?.option_id) {
    const resolvedOptionId = resolveMetricOptionIdForSubmit(
      currentMetric,
      previousMetric,
      isPostRequest,
      applyAutopatrolHighSensitivityDefault
    );
    if (
      !isPostRequest ||
      (resolvedOptionId !== null && resolvedOptionId !== undefined)
    ) {
      payload.option_id = resolvedOptionId;
    }
  }

  if (
    isPostRequest ||
    currentMetric.user_input_value !== previousMetric?.user_input_value
  ) {
    payload.user_input_value = isPostRequest
      ? getPostValue(
          currentMetric.user_input_value,
          previousMetric?.user_input_value
        )
      : currentMetric.user_input_value;
  }

  if (isPostRequest || currentMetric.distance !== previousMetric?.distance) {
    payload.distance = isPostRequest
      ? getPostValue(currentMetric.distance, previousMetric?.distance)
      : currentMetric.distance ?? null;
  }

  if (
    isPostRequest ||
    JSON.stringify(currentMetric.distance_options) !==
      JSON.stringify(previousMetric?.distance_options)
  ) {
    payload.distance_options = isPostRequest
      ? getPostValue(
          currentMetric.distance_options,
          previousMetric?.distance_options
        )
      : currentMetric.distance_options ??
        previousMetric?.distance_options ??
        null;
  }

  if (hasDoubleTakeChanged(currentMetric, previousMetric)) {
    payload.vlm_enabled = currentMetric.vlm_enabled;
  }

  return payload;
};

export const revertFailedProductMetricChanges = (
  productsData: any[],
  backupProductsData: any[],
  failedMetricKeys: string[]
) => {
  const failedKeySet = new Set(failedMetricKeys);
  const backupMetricMap = new Map<string, Record<string, any>>();

  backupProductsData.forEach((product: any) => {
    Object.values(product.metrics || {}).forEach((metric: any) => {
      const metricAsRecord = metric as Record<string, any>;
      backupMetricMap.set(createMetricKey(metricAsRecord), metricAsRecord);
    });
  });

  return productsData.map((product: any) => {
    const updatedMetrics = { ...product.metrics };

    Object.keys(updatedMetrics).forEach((metricObjectKey) => {
      const metric = updatedMetrics[metricObjectKey];
      const metricAsRecord = metric as Record<string, any>;
      const metricKey = createMetricKey(metricAsRecord);
      if (failedKeySet.has(metricKey) && backupMetricMap.has(metricKey)) {
        updatedMetrics[metricObjectKey] = {
          ...backupMetricMap.get(metricKey),
        };
      }
    });

    return {
      ...product,
      metrics: updatedMetrics,
    };
  });
};

type SuccessfulMetricUpdate = {
  metricChange: ProductMetricChange;
  metricResponse: Record<string, any>;
};

const cloneProductsData = (products: any[]) =>
  JSON.parse(JSON.stringify(products));

const metricMatchesUpdate = (
  metric: Record<string, any>,
  successfulUpdate: SuccessfulMetricUpdate
) => {
  const responseMetric = successfulUpdate.metricResponse;
  const metricResponseId = responseMetric?.id ?? null;

  if (metricResponseId && metric.id === metricResponseId) {
    return true;
  }

  const originalMetricKey = successfulUpdate.metricChange.metricKey;
  return (
    createMetricKey(metric) === originalMetricKey ||
    metric.label_name === responseMetric?.label_name
  );
};

export const mergeSuccessfulMetricResponsesIntoProducts = (
  productsData: any[],
  successfulMetricUpdates: SuccessfulMetricUpdate[]
) => {
  if (successfulMetricUpdates.length === 0) {
    return productsData;
  }

  return productsData.map((product) => {
    const updatedMetrics = { ...product.metrics };

    Object.keys(updatedMetrics).forEach((metricObjectKey) => {
      const metric = updatedMetrics[metricObjectKey];
      const successfulUpdate = successfulMetricUpdates.find((update) =>
        metricMatchesUpdate(metric as Record<string, any>, update)
      );

      if (successfulUpdate) {
        updatedMetrics[metricObjectKey] = {
          ...metric,
          ...successfulUpdate.metricResponse,
        };
      }
    });

    return {
      ...product,
      metrics: updatedMetrics,
    };
  });
};

const hasNonStreamGeneralInfoChanges = (patchObject: Record<string, any>) => {
  return Object.keys(patchObject || {}).some((key) => key !== "streams");
};

export const handleEditCameraSaveChangesButtonClicked = async (
  setEditMode: any,
  clickedTab: any,
  setActiveTab: any,
  siteInfo: any,
  cameraGeneralInfo: any,
  setBackdropLoadingScreenOn: any,
  generalInfoChangesMade: any,
  patchObject: any,
  setGeneralInfoChangesMade: any,
  setPatchObject: any,
  navigate: any,
  setCameraGeneralInfo: any,
  cameraGeneralInfoBackup: any,
  setCameraGeneralInfoBackup: any,
  immixAlerts: any,
  diceAlerts: any,
  activeChangeMade: any,
  sureviewAlerts: any,
  setIgnoreZonesBreadCrumbs: any,
  handleSitesBreadcrumbClick: any,
  handleSingularSiteBreadcrumbClick: any,
  setAddImmixAlertDialogOpen: any,
  setAddDiceAlertDialogOpen: any,
  setAddSureviewAlertDialogOpen: any,
  activeProducts: any,
  setStreamsObject: any,
  setActiveProducts: any,
  setImmixAlertsOnCamera: any,
  setDiceAlertsOnCamera: any,
  setSureViewAlertsOnCamera: any,
  setSendingImmixTest: Dispatcher<boolean>,
  setSendingDiceTest: Dispatcher<boolean>,
  setImmixTestSuccessDialogOpen: Dispatcher<boolean>,
  setDiceTestSuccessDialogOpen: Dispatcher<boolean>,
  setImmixTestFailDialogOpen: Dispatcher<boolean>,
  setDiceTestFailDialogOpen: Dispatcher<boolean>,
  setSrcImages: any,
  setCameraInfoRow: any,
  width: any,
  permissions: any,
  cameraWebhooksChangesMade: boolean,
  cameraWebhooksPatchObject: any,
  recipientsConfiguredData: any,
  titlesArray: any,
  setRecipientsConfiguredData: any,
  setTitlesArray: any,
  actionRulesData: any,
  setActionRulesData: any,
  actionRulesTitlesArray: any,
  setActionRulesTitlesArray: any,
  applicationsConfiguredData: any,
  axisFinished: any,
  timeTablesData: any,
  setTimeTablesData: any,
  iPNotifyData: any,
  setIPNotifyData: any,
  actionHandlerData: any,
  setActionHandlerData: any,
  cameraWebhookData: any,
  setCameraWebhookData: any,
  setCameraWebhooksPatchObject: any,
  setCameraWebhooksChangesMade: Dispatcher<boolean>,
  productsData: any,
  setProductsData: Dispatcher<any>,
  allTabsOpen: boolean,
  setAllTabsOpen: Dispatcher<boolean>,
  backupProductsData: any,
  setBackupProductsData: Dispatcher<any>,
  setSentinelAlertsOnCamera: Dispatcher<boolean>,
  sentinelAlerts: any,
  setAddSentinelAlertDialogOpen: Dispatcher<boolean>,
  setBoldAlertsOnCamera: Dispatcher<boolean>,
  boldAlerts: any,
  setAddBoldAlertDialogOpen: Dispatcher<boolean>,
  setPatriotAlertsOnCamera: Dispatcher<any>,
  patriotAlerts: any,
  setPatriotAlerts: Dispatcher<any>,
  setAddPatriotAlertDialogOpen: Dispatcher<boolean>,
  setUSMonitoringAlertsOnCamera: Dispatcher<any>,
  uSMonitoringAlerts: any,
  setAddUSMonitoringAlertDialogOpen: Dispatcher<boolean>,
  setSoftguardAlertsOnCamera: Dispatcher<any>,
  softguardAlerts: any,
  setAddSoftguardAlertDialogOpen: Dispatcher<boolean>,
  setSoftguardAlerts: Dispatcher<any>,
  streamParametersData: any,
  streamParametersChangesMade: boolean,
  setStreamParametersData: Dispatcher<any>,
  setStreamParametersChangesMade: Dispatcher<boolean>,
  streamParametersPatchObject: any,
  setAddStreamWebhookDialogOpen: Dispatcher<boolean>,
  setEditStreamWebhookDialogOpen: Dispatcher<boolean>,
  setDeleteImmixAlertDialogOpen: Dispatcher<boolean>,
  setDeleteDiceAlertDialogOpen: Dispatcher<boolean>,
  immixAlertsOnCamera: boolean,
  diceAlertsOnCamera: boolean,
  sureViewAlertsOnCamera: boolean,
  webhooksAlertsOnCamera: boolean,
  sentinelAlertsOnCamera: boolean,
  boldAlertsOnCamera: boolean,
  patriotAlertsOnCamera: boolean,
  softguardAlertsOnCamera: boolean,
  evalinkAlertsOnCamera: boolean,
  setEvalinkAlertsOnCamera: Dispatcher<boolean>,
  setCameraObject: Dispatcher<any>,
  saveProgress?: EditCameraSaveProgressHandlers
) => {
  // Client-side validation: block save when an edited Live Stream URL has an
  // unsupported scheme. Unedited URLs never enter patchObject, so an existing
  // (masked-for-display) value can never fail this check or be re-sent.
  if (
    patchObject?.livestream_url !== undefined &&
    !isValidLivestreamUrl(patchObject.livestream_url)
  ) {
    alert(LIVESTREAM_URL_ERROR_MESSAGE);
    return;
  }
  const AxiosUI = createApi(``);
  const closeLoading = () => {
    setBackdropLoadingScreenOn(false);
    clearEditCameraSaveProgress(saveProgress);
  };
  setBackdropLoadingScreenOn(true);
  reportEditCameraSaveStart(saveProgress);
  const alarmOptions = JSON.parse(localStorage.alarmOptions);

  const alarmIds = new Map();
  alarmOptions.forEach((object: any) => {
    alarmIds.set(object.value, object.name);
  });

  const { setAllIgnoreZones, setVisibleIgnoreZones } =
    useSiteAboutStore.getState();

  const retryFunction = () => {
    handleEditCameraSaveChangesButtonClicked(
      setEditMode,
      clickedTab,
      setActiveTab,
      siteInfo,
      cameraGeneralInfo,
      setBackdropLoadingScreenOn,
      generalInfoChangesMade,
      patchObject,
      setGeneralInfoChangesMade,
      setPatchObject,
      navigate,
      setCameraGeneralInfo,
      cameraGeneralInfoBackup,
      setCameraGeneralInfoBackup,
      immixAlerts,
      diceAlerts,
      activeChangeMade,
      sureviewAlerts,
      setIgnoreZonesBreadCrumbs,
      handleSitesBreadcrumbClick,
      handleSingularSiteBreadcrumbClick,
      setAddImmixAlertDialogOpen,
      setAddDiceAlertDialogOpen,
      setAddSureviewAlertDialogOpen,
      activeProducts,
      setStreamsObject,
      setActiveProducts,
      setImmixAlertsOnCamera,
      setDiceAlertsOnCamera,
      setSureViewAlertsOnCamera,
      setSendingImmixTest,
      setSendingDiceTest,
      setImmixTestSuccessDialogOpen,
      setDiceTestSuccessDialogOpen,
      setImmixTestFailDialogOpen,
      setDiceTestFailDialogOpen,
      setSrcImages,
      setCameraInfoRow,
      width,
      permissions,
      cameraWebhooksChangesMade,
      cameraWebhooksPatchObject,
      recipientsConfiguredData,
      titlesArray,
      setRecipientsConfiguredData,
      setTitlesArray,
      actionRulesData,
      setActionRulesData,
      actionRulesTitlesArray,
      setActionRulesTitlesArray,
      applicationsConfiguredData,
      axisFinished,
      timeTablesData,
      setTimeTablesData,
      iPNotifyData,
      setIPNotifyData,
      actionHandlerData,
      setActionHandlerData,
      cameraWebhookData,
      setCameraWebhookData,
      setCameraWebhooksPatchObject,
      setCameraWebhooksChangesMade,
      productsData,
      setProductsData,
      allTabsOpen,
      setAllTabsOpen,
      backupProductsData,
      setBackupProductsData,
      setSentinelAlertsOnCamera,
      sentinelAlerts,
      setAddSentinelAlertDialogOpen,
      setBoldAlertsOnCamera,
      boldAlerts,
      setAddBoldAlertDialogOpen,
      setPatriotAlertsOnCamera,
      patriotAlerts,
      setPatriotAlerts,
      setAddPatriotAlertDialogOpen,
      setUSMonitoringAlertsOnCamera,
      uSMonitoringAlerts,
      setAddUSMonitoringAlertDialogOpen,
      setSoftguardAlertsOnCamera,
      softguardAlerts,
      setAddSoftguardAlertDialogOpen,
      setSoftguardAlerts,
      streamParametersData,
      streamParametersChangesMade,
      setStreamParametersData,
      setStreamParametersChangesMade,
      streamParametersPatchObject,
      setAddStreamWebhookDialogOpen,
      setEditStreamWebhookDialogOpen,
      setDeleteImmixAlertDialogOpen,
      setDeleteDiceAlertDialogOpen,
      immixAlertsOnCamera,
      diceAlertsOnCamera,
      sureViewAlertsOnCamera,
      webhooksAlertsOnCamera,
      sentinelAlertsOnCamera,
      boldAlertsOnCamera,
      patriotAlertsOnCamera,
      softguardAlertsOnCamera,
      evalinkAlertsOnCamera,
      setEvalinkAlertsOnCamera,
      setCameraObject,
      saveProgress
    );
  };

  const {
    chmValueChanged,
    chmStreamMetric,
    streamChangesMade,
    handlePatchCameraStreams,
    getCameraStreams,
    setCameraStreamsObject,
    setCameraStreamsPatchObject,
    setCameraStreamsSelectedIndex,
    updatedAlertStreamTypes,
    handlePatchImmixAlerts,
    handlePatchDiceAlerts,
    handlePatchBoldAlerts,
    handlePatchPatriotAlerts,
    handlePatchSureviewAlerts,
    handlePatchSentinelAlerts,
    handlePatchSoftguardAlerts,
    handlePatchSysaidAlerts,
    handlePatchEvalinkAlerts,
    setNotesText,
    slicingChangesMade,
    slicingChanges,
    handlePatchSlicing,
    setSlicingChangesMade,
    setSlicingChanges,
    vlmChangesMade,
    patchCameraVlmConfig,
  } = useEditCameraStore.getState();

  // When patriot (or other alert-type) PATCH runs, it updates the backend; the general_info
  // patch must not send streams in the same save or it will overwrite those alert changes.
  const patriotAlertsWerePatched = !!updatedAlertStreamTypes.patriot;
  const metricChanges = getChangedProductMetrics(
    productsData,
    backupProductsData
  );
  const hasMetricChanges = metricChanges.length > 0;
  let productsDataAfterMetricSave = productsData;

  if (updatedAlertStreamTypes.immix) {
    await handlePatchImmixAlerts(navigate, setEditMode);
  }
  if (updatedAlertStreamTypes.dice) {
    await handlePatchDiceAlerts(navigate, setEditMode);
  }
  if (updatedAlertStreamTypes.bold) {
    await handlePatchBoldAlerts(navigate, setEditMode);
  }
  if (updatedAlertStreamTypes.patriot) {
    await handlePatchPatriotAlerts(navigate, setEditMode, setPatriotAlerts);
  }
  if (updatedAlertStreamTypes.sureview) {
    await handlePatchSureviewAlerts(navigate, setEditMode);
  }
  if (updatedAlertStreamTypes.sentinel) {
    await handlePatchSentinelAlerts(navigate, setEditMode);
  }
  if (updatedAlertStreamTypes.softguard) {
    await handlePatchSoftguardAlerts(navigate, setEditMode);
  }
  if (updatedAlertStreamTypes.sysaid) {
    await handlePatchSysaidAlerts(navigate, setEditMode);
  }
  if (updatedAlertStreamTypes.evalink) {
    await handlePatchEvalinkAlerts(navigate, setEditMode);
  }

  if (hasMetricChanges) {
    const fallbackStreamId = cameraGeneralInfo?.streams?.[0]?.id ?? null;
    let latestSuccessfulPostStreamId: number | null = null;
    const metricLabelToStreamIds = new Map<string, number[]>();
    (cameraGeneralInfo?.streams || []).forEach((stream: any) => {
      (stream?.stream_metrics || []).forEach((metric: any) => {
        if (!metric?.label_name) {
          return;
        }
        const existing = metricLabelToStreamIds.get(metric.label_name) || [];
        if (!existing.includes(stream.id)) {
          existing.push(stream.id);
          metricLabelToStreamIds.set(metric.label_name, existing);
        }
      });
    });
    const metricResults: PromiseSettledResult<SuccessfulMetricUpdate>[] = [];
    const totalMetricChanges = metricChanges.length;
    let completedMetricChanges = 0;
    reportProductMetricsSaveProgress(saveProgress, 0, totalMetricChanges);
    for (const metricChange of metricChanges) {
      const metricId =
        metricChange.currentMetric.id ??
        metricChange.previousMetric?.id ??
        null;
      const isPostRequest = !metricId;
      const payload = buildMetricUpdatePayload(
        metricChange.currentMetric,
        metricChange.previousMetric,
        isPostRequest,
        shouldApplyAutopatrolHighSensitivityDefault()
      );

      if (isPostRequest) {
        const candidateStreamId =
          metricLabelToStreamIds.get(metricChange.metricLabelName)?.[0] ?? null;
        payload.stream_id =
          latestSuccessfulPostStreamId ??
          candidateStreamId ??
          metricChange.currentMetric.stream_id ??
          metricChange.previousMetric?.stream_id ??
          fallbackStreamId;
      }
      try {
        const response = isPostRequest
          ? await AxiosUI.post(`/metric/`, payload)
          : await AxiosUI.patch(`/metric/${metricId}/`, payload);
        if (isPostRequest && response?.data?.stream_id) {
          latestSuccessfulPostStreamId = response.data.stream_id;
        }
        metricResults.push({
          status: "fulfilled",
          value: {
            metricChange,
            metricResponse: response.data,
          },
        });
      } catch (reason) {
        metricResults.push({
          status: "rejected",
          reason,
        });
      } finally {
        completedMetricChanges += 1;
        reportProductMetricsSaveProgress(
          saveProgress,
          completedMetricChanges,
          totalMetricChanges
        );
      }
    }

    const successfulMetricUpdates = metricResults
      .filter(
        (result): result is PromiseFulfilledResult<SuccessfulMetricUpdate> =>
          result.status === "fulfilled"
      )
      .map((result) => result.value);

    const mergedProductsData = mergeSuccessfulMetricResponsesIntoProducts(
      productsData,
      successfulMetricUpdates
    );

    if (successfulMetricUpdates.length > 0) {
      setProductsData(mergedProductsData);
      setBackupProductsData(cloneProductsData(mergedProductsData));
      productsDataAfterMetricSave = mergedProductsData;
      // Product/metric changes ride settings.json to the connector, so the
      // site now has unsynced changes. Set here (not per exit path) so every
      // metric flow — metrics-only, partial failure, metrics+general-info —
      // surfaces the Sync control.
      markSiteUnsynced();
    }

    const failedMetricChanges = metricResults
      .map((result, index) => ({ result, metricChange: metricChanges[index] }))
      .filter(({ result }) => result.status === "rejected")
      .map(({ metricChange }) => metricChange);
    if (failedMetricChanges.length > 0) {
      const failedProducts = Array.from(
        new Set(failedMetricChanges.map((change) => change.productName))
      );
      const revertedProductsData = revertFailedProductMetricChanges(
        mergedProductsData,
        backupProductsData,
        failedMetricChanges.map((change) => change.metricKey)
      );
      setProductsData(revertedProductsData);
      setBackupProductsData(cloneProductsData(revertedProductsData));
      productsDataAfterMetricSave = revertedProductsData;
      alert(
        `Failed to update these products: ${failedProducts.join(
          ", "
        )}. Reverted failed product updates.`
      );
      closeLoading();
      return;
    }
  }

  // Turn off the main loading screen after alert handlers complete
  // (individual handlers manage their own backdropLoadingScreenEditCameraOn state)
  const anyAlertUpdated =
    updatedAlertStreamTypes.immix ||
    updatedAlertStreamTypes.bold ||
    updatedAlertStreamTypes.patriot ||
    updatedAlertStreamTypes.sureview ||
    updatedAlertStreamTypes.sentinel ||
    updatedAlertStreamTypes.softguard ||
    updatedAlertStreamTypes.sysaid ||
    updatedAlertStreamTypes.evalink;

  if (
    anyAlertUpdated &&
    !slicingChangesMade &&
    !streamChangesMade &&
    !hasMetricChanges
  ) {
    closeLoading();
    // Switch back to the non-edit mode AlertSettingsTab after alert updates
    if (clickedTab === "sixteen") {
      setActiveTab(
        <ErrorBoundary>
          <AlertSettingsTab
            immixAlerts={immixAlerts}
            diceAlerts={diceAlerts}
            setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
            setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
            setSendingImmixTest={setSendingImmixTest}
            setSendingDiceTest={setSendingDiceTest}
            setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
            setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
            setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
            setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
            setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
            setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
            sureviewAlerts={sureviewAlerts}
            setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
            setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
            setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
            sentinelAlerts={sentinelAlerts}
            setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
            boldAlerts={boldAlerts}
            setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
            patriotAlerts={patriotAlerts}
            setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
            softguardAlerts={softguardAlerts}
            setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
            immixAlertsOnCamera={immixAlertsOnCamera}
            diceAlertsOnCamera={diceAlertsOnCamera}
            sureViewAlertsOnCamera={sureViewAlertsOnCamera}
            siteInfo={siteInfo}
            webhooksAlertsOnCamera={webhooksAlertsOnCamera}
            sentinelAlertsOnCamera={sentinelAlertsOnCamera}
            boldAlertsOnCamera={boldAlertsOnCamera}
            patriotAlertsOnCamera={patriotAlertsOnCamera}
            softguardAlertsOnCamera={softguardAlertsOnCamera}
            evalinkAlertsOnCamera={evalinkAlertsOnCamera}
            navigate={navigate}
          />
        </ErrorBoundary>
      );
    }
  }

  if (slicingChangesMade) {
    setBackdropLoadingScreenOn(true);
    const failedChanges: typeof slicingChanges = [];
    for (const change of slicingChanges) {
      const success = await handlePatchSlicing(
        change.streamId,
        change.slice,
        change.streamIndex,
        navigate,
        change.max_slices
      );
      if (!success) {
        failedChanges.push(change);
      }
    }
    if (failedChanges.length === 0) {
      setSlicingChangesMade(false);
      setSlicingChanges([]);
      setEditMode(false);
      if (clickedTab === "one") {
        setActiveTab(
          <ErrorBoundary>
            <GeneralInfoEditCameraTab
              siteInfo={siteInfo}
              cameraGeneralInfo={cameraGeneralInfo}
            />
          </ErrorBoundary>
        );
      } else if (clickedTab === "two") {
        setActiveTab(
          <ErrorBoundary>
            <ConnectionsTab cameraGeneralInfo={cameraGeneralInfo} />
          </ErrorBoundary>
        );
      } else if (clickedTab === "three") {
        setActiveTab(
          <ErrorBoundary>
            <NotificationsTab cameraGeneralInfo={cameraGeneralInfo} />
          </ErrorBoundary>
        );
      } else if (clickedTab === "four") {
        setActiveTab(
          <ErrorBoundary>
            <ProductsTab
              productsData={productsData}
              setProductsData={setProductsData}
              allTabsOpen={allTabsOpen}
              setAllTabsOpen={setAllTabsOpen}
              setBackupProductsData={setBackupProductsData}
            />
          </ErrorBoundary>
        );
      } else if (clickedTab === "seven") {
        setActiveTab(
          <ErrorBoundary>
            <AxisTab
              recipientsConfiguredData={recipientsConfiguredData}
              titlesArray={titlesArray}
              navigate={navigate}
              setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
              setRecipientsConfiguredData={setRecipientsConfiguredData}
              setTitlesArray={setTitlesArray}
              actionRulesData={actionRulesData}
              setActionRulesData={setActionRulesData}
              actionRulesTitlesArray={actionRulesTitlesArray}
              setActionRulesTitlesArray={setActionRulesTitlesArray}
              setActiveTab={setActiveTab}
              applicationsConfiguredData={applicationsConfiguredData}
              cameraGeneralInfo={cameraGeneralInfo}
              axisFinished={axisFinished}
            />
          </ErrorBoundary>
        );
      } else if (clickedTab === "eight") {
        setActiveTab(
          <ErrorBoundary>
            <MobotixTab
              timeTablesData={timeTablesData}
              navigate={navigate}
              setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
              setTimeTablesData={setTimeTablesData}
              setActiveTab={setActiveTab}
              iPNotifyData={iPNotifyData}
              setIPNotifyData={setIPNotifyData}
              actionHandlerData={actionHandlerData}
              setActionHandlerData={setActionHandlerData}
            />
          </ErrorBoundary>
        );
      } else if (clickedTab === "fifteen") {
        setActiveTab(
          <ErrorBoundary>
            <StreamParametersTab streamParametersData={streamParametersData} />
          </ErrorBoundary>
        );
      } else if (clickedTab === "sixteen") {
        setActiveTab(
          <ErrorBoundary>
            <AlertSettingsTab
              immixAlerts={immixAlerts}
              diceAlerts={diceAlerts}
              setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
              setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
              setSendingImmixTest={setSendingImmixTest}
              setSendingDiceTest={setSendingDiceTest}
              setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
              setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
              setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
              setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
              setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
              setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
              sureviewAlerts={sureviewAlerts}
              setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
              setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
              setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
              sentinelAlerts={sentinelAlerts}
              setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
              boldAlerts={boldAlerts}
              setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
              patriotAlerts={patriotAlerts}
              setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
              softguardAlerts={softguardAlerts}
              setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
              immixAlertsOnCamera={immixAlertsOnCamera}
              diceAlertsOnCamera={diceAlertsOnCamera}
              sureViewAlertsOnCamera={sureViewAlertsOnCamera}
              siteInfo={siteInfo}
              webhooksAlertsOnCamera={webhooksAlertsOnCamera}
              sentinelAlertsOnCamera={sentinelAlertsOnCamera}
              boldAlertsOnCamera={boldAlertsOnCamera}
              patriotAlertsOnCamera={patriotAlertsOnCamera}
              softguardAlertsOnCamera={softguardAlertsOnCamera}
              evalinkAlertsOnCamera={evalinkAlertsOnCamera}
              navigate={navigate}
            />
          </ErrorBoundary>
        );
      } else if (clickedTab === "five") {
        setActiveTab(
          <ErrorBoundary>
            <SlicingTab />
          </ErrorBoundary>
        );
      } else if (clickedTab === "seventeen") {
        setActiveTab(
          <ErrorBoundary>
            <VlmTab />
          </ErrorBoundary>
        );
      }
    } else {
      setSlicingChanges(failedChanges);
    }
    closeLoading();
  }

  if (vlmChangesMade) {
    setBackdropLoadingScreenOn(true);
    const vlmPatchSuccess = await patchCameraVlmConfig(
      Number(getNumbersAfterEditCamera(location.href)),
      navigate
    );
    if (vlmPatchSuccess) {
      setEditMode(false);
      if (clickedTab === "seventeen") {
        setActiveTab(
          <ErrorBoundary>
            <VlmTab />
          </ErrorBoundary>
        );
      }
    }
    closeLoading();
  }

  if (streamChangesMade) {
    closeLoading();
    try {
      await handlePatchCameraStreams(
        navigate,
        Number(getNumbersAfterEditCamera(location.href)),
        setEditMode
      );
      setActiveTab(
        <ErrorBoundary>
          <AlertSettingsTab
            immixAlerts={immixAlerts}
            diceAlerts={diceAlerts}
            setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
            setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
            setSendingImmixTest={setSendingImmixTest}
            setSendingDiceTest={setSendingDiceTest}
            setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
            setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
            setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
            setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
            setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
            setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
            sureviewAlerts={sureviewAlerts}
            setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
            setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
            setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
            sentinelAlerts={sentinelAlerts}
            setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
            boldAlerts={boldAlerts}
            setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
            patriotAlerts={patriotAlerts}
            setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
            softguardAlerts={softguardAlerts}
            setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
            immixAlertsOnCamera={immixAlertsOnCamera}
            diceAlertsOnCamera={diceAlertsOnCamera}
            sureViewAlertsOnCamera={sureViewAlertsOnCamera}
            siteInfo={siteInfo}
            webhooksAlertsOnCamera={webhooksAlertsOnCamera}
            sentinelAlertsOnCamera={sentinelAlertsOnCamera}
            boldAlertsOnCamera={boldAlertsOnCamera}
            patriotAlertsOnCamera={patriotAlertsOnCamera}
            softguardAlertsOnCamera={softguardAlertsOnCamera}
            evalinkAlertsOnCamera={evalinkAlertsOnCamera}
            navigate={navigate}
          />
        </ErrorBoundary>
      );
    } catch (error) {
      logger.log(`${error}`);
    }
  }
  if (cameraWebhooksChangesMade) {
    AxiosUI.patch(
      `/webhook/${cameraWebhookData.id}/`,
      cameraWebhooksPatchObject
    ).then(
      () => {
        // Stream webhooks ride settings.json (stream_webhooks) — needs a sync.
        markSiteUnsynced();
        const webHookEventTypeOptions = JSON.parse(
          localStorage.webHookEventTypeOptions
        );

        const webHookEventTypeValue = new Map();
        webHookEventTypeOptions.forEach((option: any) => {
          webHookEventTypeValue.set(option.id, option.value);
        });

        const newCameraWebhookData = cameraWebhookData;

        newCameraWebhookData.custom_fields =
          cameraWebhooksPatchObject.custom_fields;

        newCameraWebhookData.event_types = [
          webHookEventTypeValue.get(cameraWebhooksPatchObject.event_types[0]),
        ];

        setCameraWebhookData(newCameraWebhookData);

        setCameraWebhooksPatchObject({});

        setCameraWebhooksChangesMade(false);

        setEditMode(false);
        if (!activeChangeMade) {
          closeLoading();
        }
        if (clickedTab === "one") {
          setActiveTab(
            <ErrorBoundary>
              <GeneralInfoEditCameraTab
                siteInfo={siteInfo}
                cameraGeneralInfo={cameraGeneralInfo}
              />
            </ErrorBoundary>
          );
        } else if (clickedTab === "two") {
          setActiveTab(
            <ErrorBoundary>
              <ConnectionsTab cameraGeneralInfo={cameraGeneralInfo} />
            </ErrorBoundary>
          );
        } else if (clickedTab === "three") {
          setActiveTab(
            <ErrorBoundary>
              <NotificationsTab cameraGeneralInfo={cameraGeneralInfo} />
            </ErrorBoundary>
          );
        } else if (clickedTab === "four") {
          setActiveTab(
            <ErrorBoundary>
              <ProductsTab
                productsData={productsData}
                setProductsData={setProductsData}
                allTabsOpen={allTabsOpen}
                setAllTabsOpen={setAllTabsOpen}
                setBackupProductsData={setBackupProductsData}
              />
            </ErrorBoundary>
          );
        } else if (clickedTab === "seven") {
          setActiveTab(
            <ErrorBoundary>
              <AxisTab
                recipientsConfiguredData={recipientsConfiguredData}
                titlesArray={titlesArray}
                navigate={navigate}
                setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                setRecipientsConfiguredData={setRecipientsConfiguredData}
                setTitlesArray={setTitlesArray}
                actionRulesData={actionRulesData}
                setActionRulesData={setActionRulesData}
                actionRulesTitlesArray={actionRulesTitlesArray}
                setActionRulesTitlesArray={setActionRulesTitlesArray}
                setActiveTab={setActiveTab}
                applicationsConfiguredData={applicationsConfiguredData}
                cameraGeneralInfo={cameraGeneralInfo}
                axisFinished={axisFinished}
              />
            </ErrorBoundary>
          );
        } else if (clickedTab === "eight") {
          setActiveTab(
            <ErrorBoundary>
              <MobotixTab
                timeTablesData={timeTablesData}
                navigate={navigate}
                setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                setTimeTablesData={setTimeTablesData}
                setActiveTab={setActiveTab}
                iPNotifyData={iPNotifyData}
                setIPNotifyData={setIPNotifyData}
                actionHandlerData={actionHandlerData}
                setActionHandlerData={setActionHandlerData}
              />
            </ErrorBoundary>
          );
        } else if (clickedTab === "fifteen") {
          setActiveTab(
            <ErrorBoundary>
              <StreamParametersTab
                streamParametersData={streamParametersData}
              />
            </ErrorBoundary>
          );
        } else if (clickedTab === "sixteen") {
          setActiveTab(
            <ErrorBoundary>
              <AlertSettingsTab
                immixAlerts={immixAlerts}
                diceAlerts={diceAlerts}
                setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
                setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
                setSendingImmixTest={setSendingImmixTest}
                setSendingDiceTest={setSendingDiceTest}
                setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
                setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
                setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
                setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
                setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
                setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
                sureviewAlerts={sureviewAlerts}
                setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
                setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
                setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
                sentinelAlerts={sentinelAlerts}
                setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
                boldAlerts={boldAlerts}
                setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
                patriotAlerts={patriotAlerts}
                setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
                softguardAlerts={softguardAlerts}
                setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
                immixAlertsOnCamera={immixAlertsOnCamera}
                diceAlertsOnCamera={diceAlertsOnCamera}
                sureViewAlertsOnCamera={sureViewAlertsOnCamera}
                siteInfo={siteInfo}
                webhooksAlertsOnCamera={webhooksAlertsOnCamera}
                sentinelAlertsOnCamera={sentinelAlertsOnCamera}
                boldAlertsOnCamera={boldAlertsOnCamera}
                patriotAlertsOnCamera={patriotAlertsOnCamera}
                softguardAlertsOnCamera={softguardAlertsOnCamera}
                evalinkAlertsOnCamera={evalinkAlertsOnCamera}
                navigate={navigate}
              />
            </ErrorBoundary>
          );
        } else if (clickedTab === "five") {
          setActiveTab(
            <ErrorBoundary>
              <SlicingTab />
            </ErrorBoundary>
          );
        } else if (clickedTab === "seventeen") {
          setActiveTab(
            <ErrorBoundary>
              <VlmTab />
            </ErrorBoundary>
          );
        }
        closeLoading();
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          alert("Failed to submit Webhook changes, please try again.");
          setCameraWebhooksPatchObject({});
          setCameraWebhooksChangesMade(false);
          setActiveTab(
            <ErrorBoundary>
              <AlertSettingsTab
                immixAlerts={immixAlerts}
                diceAlerts={diceAlerts}
                setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
                setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
                setSendingImmixTest={setSendingImmixTest}
                setSendingDiceTest={setSendingDiceTest}
                setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
                setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
                setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
                setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
                setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
                setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
                sureviewAlerts={sureviewAlerts}
                setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
                setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
                setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
                sentinelAlerts={sentinelAlerts}
                setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
                boldAlerts={boldAlerts}
                setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
                patriotAlerts={patriotAlerts}
                setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
                softguardAlerts={softguardAlerts}
                setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
                immixAlertsOnCamera={immixAlertsOnCamera}
                diceAlertsOnCamera={diceAlertsOnCamera}
                sureViewAlertsOnCamera={sureViewAlertsOnCamera}
                siteInfo={siteInfo}
                webhooksAlertsOnCamera={webhooksAlertsOnCamera}
                sentinelAlertsOnCamera={sentinelAlertsOnCamera}
                boldAlertsOnCamera={boldAlertsOnCamera}
                patriotAlertsOnCamera={patriotAlertsOnCamera}
                softguardAlertsOnCamera={softguardAlertsOnCamera}
                evalinkAlertsOnCamera={evalinkAlertsOnCamera}
                navigate={navigate}
              />
            </ErrorBoundary>
          );
          closeLoading();
        });
      }
    );
  }
  const shouldPatchGeneralInfo =
    chmValueChanged ||
    (generalInfoChangesMade &&
      (hasNonStreamGeneralInfoChanges(patchObject) || !hasMetricChanges));

  if (shouldPatchGeneralInfo) {
    reportEditCameraSaveProgress(
      saveProgress,
      hasMetricChanges ? 88 : 50,
      hasMetricChanges ? "Saving camera settings..." : "Saving changes..."
    );
    let postPatchObjectFail = false;
    let getCameraInfoFail = false;
    let getSiteCamerasFail = false;
    // Store the updated camera info from the API response so it can be used
    // in later success handlers (closure variables remain stale)
    let updatedCameraGeneralInfo = cameraGeneralInfo;
    const updatedPatchObject = patchObject;
    if (chmValueChanged) {
      if (!updatedPatchObject.streams) {
        updatedPatchObject.streams = cameraGeneralInfo?.streams?.length
          ? [cameraGeneralInfo.streams[0]]
          : [{ stream_metrics: [] }];
      }
      if (!updatedPatchObject.streams[0]) {
        updatedPatchObject.streams[0] = { stream_metrics: [] };
      }
      if (!updatedPatchObject.streams[0].stream_metrics) {
        updatedPatchObject.streams[0].stream_metrics = [];
      }
      updatedPatchObject.streams[0].stream_metrics.push(chmStreamMetric);
    }
    // If patriot alerts were patched this save, omit streams from general_info payload
    // so we do not overwrite the alert data that was just saved via /patriot/:id/.
    const generalInfoPayload = buildGeneralInfoPayload(
      updatedPatchObject,
      patriotAlertsWerePatched,
      chmValueChanged
    );
    await AxiosUI.patch(
      `/camera/${getNumbersAfterEditCamera(location.href)}/general_info/`,
      generalInfoPayload
    ).then(
      (response: AxiosResponse) => {
        setNotesText(response.data.notes);
        setCameraStreamsObject(response.data.streams);
        setCameraStreamsPatchObject(response.data.streams);
        postPatchObjectFail = false;
        setCameraStreamsSelectedIndex(0);
        getCameraStreams(
          navigate,
          Number(getNumbersAfterEditCamera(location.href))
        );
        AxiosUI.get(
          `/camera/${getNumbersAfterEditCamera(location.href)}/ignore_zones/`
        ).then(
          (response: AxiosResponse) => {
            setAllIgnoreZones(addIgnoreLabel(response.data));
            setVisibleIgnoreZones(addIgnoreLabel(response.data));
            localStorage.setItem(
              "ignoreZones",
              JSON.stringify(addIgnoreLabel(response.data))
            );
          },
          (reason: AxiosError) => {
            universalAPIErrorHandler(reason, navigate, retryFunction, () => {});
          }
        );
      },
      (reason: AxiosError) => {
        postPatchObjectFail = true;
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          setEditMode(false);
          setCameraGeneralInfo(cameraGeneralInfoBackup);
          setPatchObject({
            camera_name: cameraGeneralInfoBackup.camera_name,
            streams: cameraGeneralInfoBackup.streams,
          });
          if (clickedTab === "one") {
            setActiveTab(
              <ErrorBoundary>
                <GeneralInfoEditCameraTab
                  siteInfo={siteInfo}
                  cameraGeneralInfo={cameraGeneralInfoBackup}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "two") {
            setActiveTab(
              <ErrorBoundary>
                <ConnectionsTab cameraGeneralInfo={cameraGeneralInfoBackup} />
              </ErrorBoundary>
            );
          } else if (clickedTab === "three") {
            setActiveTab(
              <ErrorBoundary>
                <NotificationsTab cameraGeneralInfo={cameraGeneralInfoBackup} />
              </ErrorBoundary>
            );
          } else if (clickedTab === "four") {
            setActiveTab(
              <ErrorBoundary>
                <ProductsTab
                  productsData={productsData}
                  setProductsData={setProductsData}
                  allTabsOpen={allTabsOpen}
                  setAllTabsOpen={setAllTabsOpen}
                  setBackupProductsData={setBackupProductsData}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "seven") {
            setActiveTab(
              <ErrorBoundary>
                <AxisTab
                  recipientsConfiguredData={recipientsConfiguredData}
                  titlesArray={titlesArray}
                  navigate={navigate}
                  setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                  setRecipientsConfiguredData={setRecipientsConfiguredData}
                  setTitlesArray={setTitlesArray}
                  actionRulesData={actionRulesData}
                  setActionRulesData={setActionRulesData}
                  actionRulesTitlesArray={actionRulesTitlesArray}
                  setActionRulesTitlesArray={setActionRulesTitlesArray}
                  setActiveTab={setActiveTab}
                  applicationsConfiguredData={applicationsConfiguredData}
                  cameraGeneralInfo={cameraGeneralInfo}
                  axisFinished={axisFinished}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "eight") {
            setActiveTab(
              <ErrorBoundary>
                <MobotixTab
                  timeTablesData={timeTablesData}
                  navigate={navigate}
                  setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                  setTimeTablesData={setTimeTablesData}
                  setActiveTab={setActiveTab}
                  iPNotifyData={iPNotifyData}
                  setIPNotifyData={setIPNotifyData}
                  actionHandlerData={actionHandlerData}
                  setActionHandlerData={setActionHandlerData}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "fifteen") {
            setActiveTab(
              <ErrorBoundary>
                <StreamParametersTab
                  streamParametersData={streamParametersData}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "sixteen") {
            let newSoftguardAlerts = softguardAlerts;
            if (localStorage.softguardAlertsBackup) {
              newSoftguardAlerts = JSON.parse(
                localStorage.softguardAlertsBackup
              );
              setSoftguardAlerts(newSoftguardAlerts);
            }
            setActiveTab(
              <ErrorBoundary>
                <AlertSettingsTab
                  immixAlerts={immixAlerts}
                  diceAlerts={diceAlerts}
                  setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
                  setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
                  setSendingImmixTest={setSendingImmixTest}
                  setSendingDiceTest={setSendingDiceTest}
                  setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
                  setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
                  setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
                  setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
                  setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
                  setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
                  sureviewAlerts={sureviewAlerts}
                  setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
                  setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
                  setEditStreamWebhookDialogOpen={
                    setEditStreamWebhookDialogOpen
                  }
                  sentinelAlerts={sentinelAlerts}
                  setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
                  boldAlerts={boldAlerts}
                  setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
                  patriotAlerts={patriotAlerts}
                  setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
                  softguardAlerts={softguardAlerts}
                  setAddSoftguardAlertDialogOpen={
                    setAddSoftguardAlertDialogOpen
                  }
                  immixAlertsOnCamera={immixAlertsOnCamera}
                  diceAlertsOnCamera={diceAlertsOnCamera}
                  sureViewAlertsOnCamera={sureViewAlertsOnCamera}
                  siteInfo={siteInfo}
                  webhooksAlertsOnCamera={webhooksAlertsOnCamera}
                  sentinelAlertsOnCamera={sentinelAlertsOnCamera}
                  boldAlertsOnCamera={boldAlertsOnCamera}
                  patriotAlertsOnCamera={patriotAlertsOnCamera}
                  softguardAlertsOnCamera={softguardAlertsOnCamera}
                  evalinkAlertsOnCamera={evalinkAlertsOnCamera}
                  navigate={navigate}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "five") {
            setActiveTab(
              <ErrorBoundary>
                <SlicingTab />
              </ErrorBoundary>
            );
          } else if (clickedTab === "seventeen") {
            setActiveTab(
              <ErrorBoundary>
                <VlmTab />
              </ErrorBoundary>
            );
          }
          closeLoading();
          // alert("Failed to submit changes.  Redo changes and try again");
          alert(`${reason}`);
        });
      }
    );
    !postPatchObjectFail &&
      (await AxiosUI.get(`/camera/${cameraGeneralInfo.id}/general_info/`).then(
        (response: any) => {
          const cameraObject = response.data;
          // Update the shared variable so later success handlers can access the new data
          updatedCameraGeneralInfo = cameraObject;
          setNotesText(cameraObject.notes);
          localStorage.setItem("currentCamera", response.data.camera_name);
          setCameraGeneralInfo(cameraObject);
          const { setCameraStreamsObject, setCameraStreamsPatchObject } =
            useEditCameraStore.getState();
          setCameraStreamsObject(cameraObject.streams);
          setCameraStreamsPatchObject(cameraObject.streams);

          setCameraObject(cameraObject);
          const resolvedDefaultAlarmName = getDefaultAlarmName(
            cameraObject.default_alarm
          );
          if (resolvedDefaultAlarmName === "Immix") {
            setImmixAlertsOnCamera(true);
          }
          if (resolvedDefaultAlarmName === "Dice") {
            setDiceAlertsOnCamera(true);
          }
          if (resolvedDefaultAlarmName === "SureView") {
            setSureViewAlertsOnCamera(true);
          }
          if (resolvedDefaultAlarmName === "Sentinel") {
            setSentinelAlertsOnCamera(true);
          }
          if (resolvedDefaultAlarmName === "Evalink") {
            setEvalinkAlertsOnCamera(true);
          }
          if (resolvedDefaultAlarmName === "Sysaid") {
            useEditCameraStore.getState().setSysaidAlertsOnCamera(true);
          }
          // Same builder as the load path, so the header row the camera is
          // re-rendered with after a save matches the cells.
          const connectionsTitlesArray = buildConnectionsTitlesArray(
            cameraObject,
            siteInfo
          );
          localStorage.setItem(
            "connectionsTitlesArray",
            JSON.stringify(connectionsTitlesArray)
          );
          setCameraGeneralInfoBackup(cameraObject);
          const pushedArrayImmix: any[] = [];
          const pushedArrayDice: any[] = [];
          const pushedArraySentinel: any[] = [];
          if (cameraObject.streams[0]) {
            if (cameraObject.streams[0].immix_alerts[0]) {
              cameraObject.streams[0].immix_alerts.forEach((object: any) => {
                pushedArrayImmix.push(object);
              });
            }
            if (cameraObject.streams[0].dice_alerts?.[0]) {
              cameraObject.streams[0].dice_alerts?.forEach((object: any) => {
                pushedArrayDice.push(object);
              });
            }
            if (cameraObject.streams[0].sentinel_alerts[0]) {
              cameraObject.streams[0].sentinel_alerts.forEach((object: any) => {
                pushedArraySentinel.push(object);
              });
            }
          }
          if (cameraObject.streams[0]) {
            cameraObject.streams.forEach((streamObject: any) => {
              if (streamObject.sureview_alerts[0]) {
                localStorage.setItem(
                  "sureViewStreamObject",
                  JSON.stringify(streamObject)
                );
              } else if (streamObject.immix_alerts[0]) {
                localStorage.setItem(
                  "immixStreamObject",
                  JSON.stringify(streamObject)
                );
              } else if (streamObject.dice_alerts?.[0]) {
                localStorage.setItem(
                  "diceStreamObject",
                  JSON.stringify(streamObject)
                );
              }
            });
          }

          const sureViewPushedArray: any[] = [];
          if (cameraObject.streams[0]) {
            if (cameraObject.streams[0].sureview_alerts[0]) {
              cameraObject.streams[0].sureview_alerts.forEach((object: any) => {
                sureViewPushedArray.push(object);
              });
            }
          }

          generateActiveProducts(
            cameraObject.streams,
            setActiveProducts,
            setProductsData,
            setBackupProductsData
          );
          getCameraInfoFail = false;
        },
        (reason: AxiosError) => {
          if (isResourceNotFoundError(reason)) {
            navigateToResourceNotFound(
              navigate,
              "camera",
              cameraGeneralInfo.id
            );
          } else {
            universalAPIErrorHandler(reason, navigate, retryFunction, () => {
              alert("Error retrieving site data");
              getCameraInfoFail = true;
              navigate("/");
            });
          }
        }
      ));
    !postPatchObjectFail &&
      !getCameraInfoFail &&
      (await AxiosUI.get(
        `camera/site/?customer__id=${getNumbersAfterSites(location.href)}`
      ).then(
        (response: AxiosResponse) => {
          getSiteCamerasFail = false;
          localStorage.setItem("siteCameras", JSON.stringify(response.data));
        },
        (reason: AxiosError) => {
          getSiteCamerasFail = true;
          universalAPIErrorHandler(reason, navigate, retryFunction, () => {});
        }
      ));
    // Saving no longer auto-syncs. Mark the site as having unsynced changes;
    // the operator syncs explicitly via the edit-view Sync control.
    if (!postPatchObjectFail && !getCameraInfoFail && !getSiteCamerasFail) {
      markSiteUnsynced();
    }
    !postPatchObjectFail &&
      !getCameraInfoFail &&
      !getSiteCamerasFail &&
      (await AxiosUI.get(
        `customer/${getNumbersAfterSites(location.href)}/about/`
      ).then(
        (response: AxiosResponse) => {
          localStorage.setItem("siteInfo", JSON.stringify(response.data));
          localStorage.setItem(
            "integrationType",
            response.data.integration_type
          );
          setIgnoreZonesBreadCrumbs(
            <IgnoreZonesBreadCrumbs
              linksArray={createIgnoreZonesLinksArray(
                response.data.breadcrumbs
              )}
              handleSitesBreadcrumbClick={handleSitesBreadcrumbClick}
              handleSingularSiteBreadcrumbClick={
                handleSingularSiteBreadcrumbClick
              }
              styling="absolute top-[10px] left-[30px]"
            />
          );
          setEditMode(false);
          if (!activeChangeMade) {
            closeLoading();
          }
          if (clickedTab === "one") {
            setActiveTab(
              <ErrorBoundary>
                <GeneralInfoEditCameraTab
                  siteInfo={siteInfo}
                  cameraGeneralInfo={updatedCameraGeneralInfo}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "two") {
            setActiveTab(
              <ErrorBoundary>
                <ConnectionsTab cameraGeneralInfo={updatedCameraGeneralInfo} />
              </ErrorBoundary>
            );
          } else if (clickedTab === "three") {
            setActiveTab(
              <ErrorBoundary>
                <NotificationsTab
                  cameraGeneralInfo={updatedCameraGeneralInfo}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "four") {
            setActiveTab(
              <ErrorBoundary>
                <ProductsTab
                  productsData={productsData}
                  setProductsData={setProductsData}
                  allTabsOpen={allTabsOpen}
                  setAllTabsOpen={setAllTabsOpen}
                  setBackupProductsData={setBackupProductsData}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "seven") {
            setActiveTab(
              <ErrorBoundary>
                <AxisTab
                  recipientsConfiguredData={recipientsConfiguredData}
                  titlesArray={titlesArray}
                  navigate={navigate}
                  setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                  setRecipientsConfiguredData={setRecipientsConfiguredData}
                  setTitlesArray={setTitlesArray}
                  actionRulesData={actionRulesData}
                  setActionRulesData={setActionRulesData}
                  actionRulesTitlesArray={actionRulesTitlesArray}
                  setActionRulesTitlesArray={setActionRulesTitlesArray}
                  setActiveTab={setActiveTab}
                  applicationsConfiguredData={applicationsConfiguredData}
                  cameraGeneralInfo={updatedCameraGeneralInfo}
                  axisFinished={axisFinished}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "eight") {
            setActiveTab(
              <ErrorBoundary>
                <MobotixTab
                  timeTablesData={timeTablesData}
                  navigate={navigate}
                  setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                  setTimeTablesData={setTimeTablesData}
                  setActiveTab={setActiveTab}
                  iPNotifyData={iPNotifyData}
                  setIPNotifyData={setIPNotifyData}
                  actionHandlerData={actionHandlerData}
                  setActionHandlerData={setActionHandlerData}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "fifteen") {
            setActiveTab(
              <ErrorBoundary>
                <StreamParametersTab
                  streamParametersData={streamParametersData}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "sixteen") {
            setActiveTab(
              <ErrorBoundary>
                <AlertSettingsTab
                  immixAlerts={immixAlerts}
                  diceAlerts={diceAlerts}
                  setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
                  setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
                  setSendingImmixTest={setSendingImmixTest}
                  setSendingDiceTest={setSendingDiceTest}
                  setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
                  setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
                  setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
                  setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
                  setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
                  setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
                  sureviewAlerts={sureviewAlerts}
                  setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
                  setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
                  setEditStreamWebhookDialogOpen={
                    setEditStreamWebhookDialogOpen
                  }
                  sentinelAlerts={sentinelAlerts}
                  setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
                  boldAlerts={boldAlerts}
                  setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
                  patriotAlerts={patriotAlerts}
                  setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
                  softguardAlerts={softguardAlerts}
                  setAddSoftguardAlertDialogOpen={
                    setAddSoftguardAlertDialogOpen
                  }
                  immixAlertsOnCamera={immixAlertsOnCamera}
                  diceAlertsOnCamera={diceAlertsOnCamera}
                  sureViewAlertsOnCamera={sureViewAlertsOnCamera}
                  siteInfo={siteInfo}
                  webhooksAlertsOnCamera={webhooksAlertsOnCamera}
                  sentinelAlertsOnCamera={sentinelAlertsOnCamera}
                  boldAlertsOnCamera={boldAlertsOnCamera}
                  patriotAlertsOnCamera={patriotAlertsOnCamera}
                  softguardAlertsOnCamera={softguardAlertsOnCamera}
                  evalinkAlertsOnCamera={evalinkAlertsOnCamera}
                  navigate={navigate}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "five") {
            setActiveTab(
              <ErrorBoundary>
                <SlicingTab />
              </ErrorBoundary>
            );
          } else if (clickedTab === "seventeen") {
            setActiveTab(
              <ErrorBoundary>
                <VlmTab />
              </ErrorBoundary>
            );
          }
        },
        (reason: AxiosError) => {
          universalAPIErrorHandler(reason, navigate, retryFunction, () => {
            if (clickedTab === "one") {
              setActiveTab(
                <ErrorBoundary>
                  <GeneralInfoEditCameraTab
                    siteInfo={siteInfo}
                    cameraGeneralInfo={cameraGeneralInfo}
                  />
                </ErrorBoundary>
              );
            } else if (clickedTab === "two") {
              setActiveTab(
                <ErrorBoundary>
                  <ConnectionsTab cameraGeneralInfo={cameraGeneralInfo} />
                </ErrorBoundary>
              );
            } else if (clickedTab === "three") {
              setActiveTab(
                <ErrorBoundary>
                  <NotificationsTab cameraGeneralInfo={cameraGeneralInfo} />
                </ErrorBoundary>
              );
            } else if (clickedTab === "four") {
              setActiveTab(
                <ErrorBoundary>
                  <ProductsTab
                    productsData={productsData}
                    setProductsData={setProductsData}
                    allTabsOpen={allTabsOpen}
                    setAllTabsOpen={setAllTabsOpen}
                    setBackupProductsData={setBackupProductsData}
                  />
                </ErrorBoundary>
              );
            } else if (clickedTab === "seven") {
              setActiveTab(
                <ErrorBoundary>
                  <AxisTab
                    recipientsConfiguredData={recipientsConfiguredData}
                    titlesArray={titlesArray}
                    navigate={navigate}
                    setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                    setRecipientsConfiguredData={setRecipientsConfiguredData}
                    setTitlesArray={setTitlesArray}
                    actionRulesData={actionRulesData}
                    setActionRulesData={setActionRulesData}
                    actionRulesTitlesArray={actionRulesTitlesArray}
                    setActionRulesTitlesArray={setActionRulesTitlesArray}
                    setActiveTab={setActiveTab}
                    applicationsConfiguredData={applicationsConfiguredData}
                    cameraGeneralInfo={cameraGeneralInfo}
                    axisFinished={axisFinished}
                  />
                </ErrorBoundary>
              );
            } else if (clickedTab === "eight") {
              setActiveTab(
                <ErrorBoundary>
                  <MobotixTab
                    timeTablesData={timeTablesData}
                    navigate={navigate}
                    setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                    setTimeTablesData={setTimeTablesData}
                    setActiveTab={setActiveTab}
                    iPNotifyData={iPNotifyData}
                    setIPNotifyData={setIPNotifyData}
                    actionHandlerData={actionHandlerData}
                    setActionHandlerData={setActionHandlerData}
                  />
                </ErrorBoundary>
              );
            } else if (clickedTab === "fifteen") {
              setActiveTab(
                <ErrorBoundary>
                  <StreamParametersTab
                    streamParametersData={streamParametersData}
                  />
                </ErrorBoundary>
              );
            } else if (clickedTab === "sixteen") {
              setActiveTab(
                <ErrorBoundary>
                  <AlertSettingsTab
                    immixAlerts={immixAlerts}
                    diceAlerts={diceAlerts}
                    setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
                    setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
                    setSendingImmixTest={setSendingImmixTest}
                    setSendingDiceTest={setSendingDiceTest}
                    setImmixTestSuccessDialogOpen={
                      setImmixTestSuccessDialogOpen
                    }
                    setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
                    setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
                    setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
                    setDeleteImmixAlertDialogOpen={
                      setDeleteImmixAlertDialogOpen
                    }
                    setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
                    sureviewAlerts={sureviewAlerts}
                    setAddSureviewAlertDialogOpen={
                      setAddSureviewAlertDialogOpen
                    }
                    setAddStreamWebhookDialogOpen={
                      setAddStreamWebhookDialogOpen
                    }
                    setEditStreamWebhookDialogOpen={
                      setEditStreamWebhookDialogOpen
                    }
                    sentinelAlerts={sentinelAlerts}
                    setAddSentinelAlertDialogOpen={
                      setAddSentinelAlertDialogOpen
                    }
                    boldAlerts={boldAlerts}
                    setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
                    patriotAlerts={patriotAlerts}
                    setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
                    softguardAlerts={softguardAlerts}
                    setAddSoftguardAlertDialogOpen={
                      setAddSoftguardAlertDialogOpen
                    }
                    immixAlertsOnCamera={immixAlertsOnCamera}
                    diceAlertsOnCamera={diceAlertsOnCamera}
                    sureViewAlertsOnCamera={sureViewAlertsOnCamera}
                    siteInfo={siteInfo}
                    webhooksAlertsOnCamera={webhooksAlertsOnCamera}
                    sentinelAlertsOnCamera={sentinelAlertsOnCamera}
                    boldAlertsOnCamera={boldAlertsOnCamera}
                    patriotAlertsOnCamera={patriotAlertsOnCamera}
                    softguardAlertsOnCamera={softguardAlertsOnCamera}
                    evalinkAlertsOnCamera={evalinkAlertsOnCamera}
                    navigate={navigate}
                  />
                </ErrorBoundary>
              );
            } else if (clickedTab === "five") {
              setActiveTab(
                <ErrorBoundary>
                  <SlicingTab />
                </ErrorBoundary>
              );
            } else if (clickedTab === "seventeen") {
              setActiveTab(
                <ErrorBoundary>
                  <VlmTab />
                </ErrorBoundary>
              );
            }
            closeLoading();
          });
        }
      ));
  }
  if (hasMetricChanges && !shouldPatchGeneralInfo) {
    try {
      reportEditCameraSaveRefreshing(saveProgress);
      const cameraInfoResponse = await AxiosUI.get(
        `/camera/${cameraGeneralInfo.id}/general_info/`
      );
      const refreshedCameraInfo = cameraInfoResponse.data;
      setCameraGeneralInfo(refreshedCameraInfo);
      setCameraGeneralInfoBackup(refreshedCameraInfo);
      setCameraObject(refreshedCameraInfo);
      setNotesText(refreshedCameraInfo.notes);
      generateActiveProducts(
        refreshedCameraInfo.streams,
        setActiveProducts,
        setProductsData,
        setBackupProductsData
      );
      setGeneralInfoChangesMade(false);
      setEditMode(false);
      if (clickedTab === "four") {
        setActiveTab(
          <ErrorBoundary>
            <ProductsTab
              productsData={productsDataAfterMetricSave}
              setProductsData={setProductsData}
              allTabsOpen={allTabsOpen}
              setAllTabsOpen={setAllTabsOpen}
              setBackupProductsData={setBackupProductsData}
            />
          </ErrorBoundary>
        );
      }
    } catch (reason: any) {
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        alert("Metric updates were saved, but camera data refresh failed.");
      });
    } finally {
      closeLoading();
    }
    return;
  }

  if (activeChangeMade) {
    let cameraActivateOrDeactivateFail = false;
    if (cameraGeneralInfo.active) {
      const submittedObject = {
        camera_ids: [editCameraIdFromUrl()],
      };
      await AxiosUI.post(`camera/action/activate/`, submittedObject).then(
        () => {
          cameraActivateOrDeactivateFail = false;
          setEditMode(false);
        },
        (reason: AxiosError) => {
          cameraActivateOrDeactivateFail = true;
          universalAPIErrorHandler(reason, navigate, retryFunction, () => {
            closeLoading();
            setCameraGeneralInfo((previousState: any) => {
              return {
                ...previousState,
                active: !cameraGeneralInfo.active,
              };
            });
          });
        }
      );
    }
    if (!cameraGeneralInfo.active) {
      const submittedObject = {
        camera_ids: [editCameraIdFromUrl()],
      };
      await AxiosUI.post(`camera/action/deactivate/`, submittedObject).then(
        () => {
          cameraActivateOrDeactivateFail = false;
          setEditMode(false);
        },
        (reason: AxiosError) => {
          cameraActivateOrDeactivateFail = true;
          universalAPIErrorHandler(reason, navigate, retryFunction, () => {
            closeLoading();
            setCameraGeneralInfo((previousState: any) => {
              return {
                ...previousState,
                active: !cameraGeneralInfo.active,
              };
            });
          });
        }
      );
    }
    // Activating/deactivating a camera no longer auto-syncs. Mark the site as
    // having unsynced changes; the operator syncs explicitly via the edit-view
    // Sync control.
    if (!cameraActivateOrDeactivateFail) {
      markSiteUnsynced();
    }
    !cameraActivateOrDeactivateFail &&
      (await AxiosUI.get(
        `camera/site/?customer__id=${getNumbersAfterSites(location.href)}`
      ).then(
        (response: AxiosResponse) => {
          localStorage.setItem("siteCameras", JSON.stringify(response.data));
          closeLoading();
        },
        (reason: AxiosError) => {
          universalAPIErrorHandler(reason, navigate, retryFunction, () => {
            closeLoading();
          });
        }
      ));
  }
  if (streamParametersChangesMade) {
    AxiosUI.patch(
      `camera/${getNumbersAfterEditCamera(location.href)}/extra_fields/`,
      streamParametersPatchObject
    ).then(
      (response: AxiosResponse) => {
        // Extra fields (e.g. milestone_motion) ride settings.json — needs a sync.
        markSiteUnsynced();
        setStreamParametersData(response.data);
        setStreamParametersChangesMade(false);
        setEditMode(false);
        closeLoading();
        if (clickedTab === "one") {
          setActiveTab(
            <ErrorBoundary>
              <GeneralInfoEditCameraTab
                siteInfo={siteInfo}
                cameraGeneralInfo={cameraGeneralInfo}
              />
            </ErrorBoundary>
          );
        } else if (clickedTab === "two") {
          setActiveTab(
            <ErrorBoundary>
              <ConnectionsTab cameraGeneralInfo={cameraGeneralInfo} />
            </ErrorBoundary>
          );
        } else if (clickedTab === "three") {
          setActiveTab(
            <ErrorBoundary>
              <NotificationsTab cameraGeneralInfo={cameraGeneralInfo} />
            </ErrorBoundary>
          );
        } else if (clickedTab === "four") {
          setActiveTab(
            <ErrorBoundary>
              <ProductsTab
                productsData={productsData}
                setProductsData={setProductsData}
                allTabsOpen={allTabsOpen}
                setAllTabsOpen={setAllTabsOpen}
                setBackupProductsData={setBackupProductsData}
              />
            </ErrorBoundary>
          );
        } else if (clickedTab === "seven") {
          setActiveTab(
            <ErrorBoundary>
              <AxisTab
                recipientsConfiguredData={recipientsConfiguredData}
                titlesArray={titlesArray}
                navigate={navigate}
                setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                setRecipientsConfiguredData={setRecipientsConfiguredData}
                setTitlesArray={setTitlesArray}
                actionRulesData={actionRulesData}
                setActionRulesData={setActionRulesData}
                actionRulesTitlesArray={actionRulesTitlesArray}
                setActionRulesTitlesArray={setActionRulesTitlesArray}
                setActiveTab={setActiveTab}
                applicationsConfiguredData={applicationsConfiguredData}
                cameraGeneralInfo={cameraGeneralInfo}
                axisFinished={axisFinished}
              />
            </ErrorBoundary>
          );
        } else if (clickedTab === "eight") {
          setActiveTab(
            <ErrorBoundary>
              <MobotixTab
                timeTablesData={timeTablesData}
                navigate={navigate}
                setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                setTimeTablesData={setTimeTablesData}
                setActiveTab={setActiveTab}
                iPNotifyData={iPNotifyData}
                setIPNotifyData={setIPNotifyData}
                actionHandlerData={actionHandlerData}
                setActionHandlerData={setActionHandlerData}
              />
            </ErrorBoundary>
          );
        } else if (clickedTab === "fifteen") {
          setActiveTab(
            <ErrorBoundary>
              <StreamParametersTab streamParametersData={response.data} />
            </ErrorBoundary>
          );
        } else if (clickedTab === "sixteen") {
          setActiveTab(
            <ErrorBoundary>
              <AlertSettingsTab
                immixAlerts={immixAlerts}
                diceAlerts={diceAlerts}
                setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
                setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
                setSendingImmixTest={setSendingImmixTest}
                setSendingDiceTest={setSendingDiceTest}
                setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
                setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
                setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
                setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
                setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
                setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
                sureviewAlerts={sureviewAlerts}
                setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
                setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
                setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
                sentinelAlerts={sentinelAlerts}
                setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
                boldAlerts={boldAlerts}
                setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
                patriotAlerts={patriotAlerts}
                setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
                softguardAlerts={softguardAlerts}
                setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
                immixAlertsOnCamera={immixAlertsOnCamera}
                diceAlertsOnCamera={diceAlertsOnCamera}
                sureViewAlertsOnCamera={sureViewAlertsOnCamera}
                siteInfo={siteInfo}
                webhooksAlertsOnCamera={webhooksAlertsOnCamera}
                sentinelAlertsOnCamera={sentinelAlertsOnCamera}
                boldAlertsOnCamera={boldAlertsOnCamera}
                patriotAlertsOnCamera={patriotAlertsOnCamera}
                softguardAlertsOnCamera={softguardAlertsOnCamera}
                evalinkAlertsOnCamera={evalinkAlertsOnCamera}
                navigate={navigate}
              />
            </ErrorBoundary>
          );
        } else if (clickedTab === "five") {
          setActiveTab(
            <ErrorBoundary>
              <SlicingTab />
            </ErrorBoundary>
          );
        } else if (clickedTab === "seventeen") {
          setActiveTab(
            <ErrorBoundary>
              <VlmTab />
            </ErrorBoundary>
          );
        }
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          if (clickedTab === "one") {
            setActiveTab(
              <ErrorBoundary>
                <GeneralInfoEditCameraTab
                  siteInfo={siteInfo}
                  cameraGeneralInfo={cameraGeneralInfo}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "two") {
            setActiveTab(
              <ErrorBoundary>
                <ConnectionsTab cameraGeneralInfo={cameraGeneralInfo} />
              </ErrorBoundary>
            );
          } else if (clickedTab === "three") {
            setActiveTab(
              <ErrorBoundary>
                <NotificationsTab cameraGeneralInfo={cameraGeneralInfo} />
              </ErrorBoundary>
            );
          } else if (clickedTab === "four") {
            setActiveTab(
              <ErrorBoundary>
                <ProductsTab
                  productsData={productsData}
                  setProductsData={setProductsData}
                  allTabsOpen={allTabsOpen}
                  setAllTabsOpen={setAllTabsOpen}
                  setBackupProductsData={setBackupProductsData}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "seven") {
            setActiveTab(
              <ErrorBoundary>
                <AxisTab
                  recipientsConfiguredData={recipientsConfiguredData}
                  titlesArray={titlesArray}
                  navigate={navigate}
                  setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                  setRecipientsConfiguredData={setRecipientsConfiguredData}
                  setTitlesArray={setTitlesArray}
                  actionRulesData={actionRulesData}
                  setActionRulesData={setActionRulesData}
                  actionRulesTitlesArray={actionRulesTitlesArray}
                  setActionRulesTitlesArray={setActionRulesTitlesArray}
                  setActiveTab={setActiveTab}
                  applicationsConfiguredData={applicationsConfiguredData}
                  cameraGeneralInfo={cameraGeneralInfo}
                  axisFinished={axisFinished}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "eight") {
            setActiveTab(
              <ErrorBoundary>
                <MobotixTab
                  timeTablesData={timeTablesData}
                  navigate={navigate}
                  setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                  setTimeTablesData={setTimeTablesData}
                  setActiveTab={setActiveTab}
                  iPNotifyData={iPNotifyData}
                  setIPNotifyData={setIPNotifyData}
                  actionHandlerData={actionHandlerData}
                  setActionHandlerData={setActionHandlerData}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "fifteen") {
            setActiveTab(
              <ErrorBoundary>
                <StreamParametersTab
                  streamParametersData={streamParametersData}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "sixteen") {
            setActiveTab(
              <ErrorBoundary>
                <AlertSettingsTab
                  immixAlerts={immixAlerts}
                  diceAlerts={diceAlerts}
                  setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
                  setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
                  setSendingImmixTest={setSendingImmixTest}
                  setSendingDiceTest={setSendingDiceTest}
                  setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
                  setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
                  setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
                  setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
                  setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
                  setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
                  sureviewAlerts={sureviewAlerts}
                  setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
                  setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
                  setEditStreamWebhookDialogOpen={
                    setEditStreamWebhookDialogOpen
                  }
                  sentinelAlerts={sentinelAlerts}
                  setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
                  boldAlerts={boldAlerts}
                  setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
                  patriotAlerts={patriotAlerts}
                  setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
                  softguardAlerts={softguardAlerts}
                  setAddSoftguardAlertDialogOpen={
                    setAddSoftguardAlertDialogOpen
                  }
                  immixAlertsOnCamera={immixAlertsOnCamera}
                  diceAlertsOnCamera={diceAlertsOnCamera}
                  sureViewAlertsOnCamera={sureViewAlertsOnCamera}
                  siteInfo={siteInfo}
                  webhooksAlertsOnCamera={webhooksAlertsOnCamera}
                  sentinelAlertsOnCamera={sentinelAlertsOnCamera}
                  boldAlertsOnCamera={boldAlertsOnCamera}
                  patriotAlertsOnCamera={patriotAlertsOnCamera}
                  softguardAlertsOnCamera={softguardAlertsOnCamera}
                  evalinkAlertsOnCamera={evalinkAlertsOnCamera}
                  navigate={navigate}
                />
              </ErrorBoundary>
            );
          } else if (clickedTab === "five") {
            setActiveTab(
              <ErrorBoundary>
                <SlicingTab />
              </ErrorBoundary>
            );
          } else if (clickedTab === "seventeen") {
            setActiveTab(
              <ErrorBoundary>
                <VlmTab />
              </ErrorBoundary>
            );
          }
          closeLoading();
        });
      }
    );
  }
  // Only call handleEditCameraCancelButtonClick when NO changes were made
  // (to exit edit mode without saving). When changes were made and saved,
  // the success handlers above already set the active tab with the updated data.
  // Calling cancel here with stale closure values would revert the UI to old data.
  if (
    !cameraWebhooksChangesMade &&
    !activeChangeMade &&
    !generalInfoChangesMade &&
    !streamParametersChangesMade &&
    !updatedAlertStreamTypes.immix &&
    !updatedAlertStreamTypes.dice &&
    !updatedAlertStreamTypes.sureview &&
    !updatedAlertStreamTypes.sentinel &&
    !updatedAlertStreamTypes.bold &&
    !updatedAlertStreamTypes.patriot &&
    !updatedAlertStreamTypes.softguard &&
    !streamChangesMade &&
    !chmValueChanged &&
    !slicingChangesMade &&
    !vlmChangesMade
  ) {
    handleEditCameraCancelButtonClick(
      setEditMode,
      clickedTab,
      setActiveTab,
      siteInfo,
      cameraGeneralInfo,
      setCameraGeneralInfo,
      cameraGeneralInfoBackup,
      setPatchObject,
      immixAlerts,
      diceAlerts,
      sureviewAlerts,
      setAddImmixAlertDialogOpen,
      setAddDiceAlertDialogOpen,
      setAddSureviewAlertDialogOpen,
      activeProducts,
      setBackdropLoadingScreenOn,
      setSendingImmixTest,
      setImmixTestSuccessDialogOpen,
      setImmixTestFailDialogOpen,
      setSendingDiceTest,
      setDiceTestSuccessDialogOpen,
      setDiceTestFailDialogOpen,
      recipientsConfiguredData,
      titlesArray,
      setRecipientsConfiguredData,
      setTitlesArray,
      actionRulesData,
      setActionRulesData,
      actionRulesTitlesArray,
      setActionRulesTitlesArray,
      applicationsConfiguredData,
      axisFinished,
      timeTablesData,
      setTimeTablesData,
      iPNotifyData,
      setIPNotifyData,
      actionHandlerData,
      setActionHandlerData,
      cameraWebhookData,
      productsData,
      setProductsData,
      allTabsOpen,
      setAllTabsOpen,
      backupProductsData,
      setBackupProductsData,
      sentinelAlerts,
      setAddSentinelAlertDialogOpen,
      boldAlerts,
      setAddBoldAlertDialogOpen,
      patriotAlerts,
      setAddPatriotAlertDialogOpen,
      uSMonitoringAlerts,
      setAddUSMonitoringAlertDialogOpen,
      softguardAlerts,
      setAddSoftguardAlertDialogOpen,
      streamParametersData,
      setAddStreamWebhookDialogOpen,
      setEditStreamWebhookDialogOpen,
      setDeleteImmixAlertDialogOpen,
      setDeleteDiceAlertDialogOpen,
      immixAlertsOnCamera,
      diceAlertsOnCamera,
      sureViewAlertsOnCamera,
      webhooksAlertsOnCamera,
      sentinelAlertsOnCamera,
      boldAlertsOnCamera,
      patriotAlertsOnCamera,
      softguardAlertsOnCamera,
      evalinkAlertsOnCamera,
      navigate
    );
  }
};
